/**
 * The signed-in user's identity, top-right of the portal — and the one entry
 * point into Profile, Settings and Logout.
 *
 * There used to be a lone gear icon here, because Settings was the only place
 * to go. Now Profile and Logout need a home too, and a dropdown under a
 * person's own name/photo is where every app puts them — Esri Portal
 * included, which is the reference this follows.
 *
 * No photo is stored anywhere in this app (`User` carries no avatar field),
 * so the "photo" is the person's own initials on a colour derived from their
 * id — real identity, not a placeholder image standing in for one.
 */

import { useRef, useState } from 'react';
import { ChevronDown, LogOut, Settings as SettingsIcon, UserRound } from 'lucide-react';
import type { User } from '../../types';
import { roleService } from '../../services/roleService';
import LayerOptionsMenu, { type LayerMenuItem } from '../layers/LayerOptionsMenu';

const AVATAR_PALETTE = ['#0EA5E9', '#6366F1', '#14B8A6', '#F59E0B', '#EC4899', '#8B5CF6', '#10B981', '#F43F5E'];

/** A stable colour from a string, so the same person's initials land on the
 *  same background every time without storing a colour anywhere. */
function avatarColour(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase();
}

export default function UserMenu({
  user,
  showSettings,
  onOpenProfile,
  onOpenSettings,
  onLogout,
}: {
  user: User;
  /** Every signed-in user has at least one section to open now (App
   *  Gallery, Layers, ArcGIS Servers, GeoServer — see SettingsPage's
   *  `everyone`-gated sections), so this stays a prop rather than a
   *  hardcoded `true` in case a future role ever has nothing at all. */
  showSettings: boolean;
  onOpenProfile: () => void;
  onOpenSettings: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const items: LayerMenuItem[] = [
    { id: 'profile', label: 'Profile', icon: UserRound, onSelect: onOpenProfile },
    ...(showSettings
      ? [{ id: 'settings', label: 'Settings', icon: SettingsIcon, onSelect: onOpenSettings }]
      : []),
    { id: 'logout', label: 'Log out', icon: LogOut, onSelect: onLogout, danger: true, separatorBefore: true },
  ];

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${user.name}'s account menu`}
        className="flex items-center gap-2 rounded-xl px-1.5 py-1 transition hover:bg-white/10"
        style={{ border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.10)' }}
      >
        <span
          aria-hidden="true"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white"
          style={{ background: avatarColour(user.id || user.email || user.name) }}
        >
          {initialsOf(user.name)}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block max-w-[140px] truncate text-[0.78rem] font-semibold leading-tight text-[#F8FAFC]">
            {user.name}
          </span>
          <span className="block text-[0.62rem] font-bold uppercase tracking-wide text-teal-300/90">
            {/* The real role NAME, not a binary admin/not-admin guess — a
                custom role (e.g. "Editor") previously always showed as
                plain "User" here, which was simply wrong, not just vague.
                Already hydrated at login (see roleService/AuthContext), so
                this is a synchronous read, no loading state needed. */}
            {roleService.myRoleName() ?? user.role}
          </span>
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 flex-shrink-0 text-white/70 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <LayerOptionsMenu
          ariaLabel="Account menu"
          anchorRef={{ current: triggerRef.current }}
          items={items}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
